import { TFunction } from 'i18next';

export interface ValidationErrors {
  name?: string;
  description?: string;
  difficulty?: string;
  categories?: string;
  image_path?: string;
  general?: string;
}

export const MAX_IMAGE_SIZE = 2 * 1024 * 1024;
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
export const FALLBACK_PREVIEW_IMG = 'https://placehold.co/32x32';

interface ValidateFormParams {
  name: string;
  description: string;
  selectedImageFile: File | null;
  t: TFunction;
}

/**
 * Validates the prefilled template form fields
 */
export const validatePrefilledForm = ({
  name,
  description,
  selectedImageFile,
  t
}: ValidateFormParams): ValidationErrors => {
  const errors: ValidationErrors = {};
  const nameRegex = /^[A-Za-z \-.]+$/;

  if (!name.trim()) {
    errors.name = t('phishingTemplateManager.prefilledForm.errors.nameRequired');
  } else if (name.length < 3) {
    errors.name = t('phishingTemplateManager.prefilledForm.errors.nameMinLength');
  } else if (name.length > 60) {
    errors.name = t('phishingTemplateManager.prefilledForm.errors.nameMaxLength');
  } else if (!nameRegex.test(name)) {
    errors.name = t('phishingTemplateManager.prefilledForm.errors.nameInvalidChars');
  }

  if (description && description.length > 1000) {
    errors.description = t('phishingTemplateManager.prefilledForm.errors.descMaxLength');
  }

  if (selectedImageFile) {
    if (!ALLOWED_IMAGE_TYPES.includes(selectedImageFile.type)) {
      errors.image_path = t('phishingTemplateManager.prefilledForm.errors.imageTypeInvalid');
    } else if (selectedImageFile.size > MAX_IMAGE_SIZE) {
      errors.image_path = t('phishingTemplateManager.prefilledForm.errors.imageSizeLimit');
    }
  }

  return errors;
};

/**
 * Parses and maps backend error responses into form field errors
 */
export const parseBackendErrors = (
  backendErrors: any
): { fieldErrors: ValidationErrors; generalError?: string } => {
  const fieldErrors: ValidationErrors = {};
  const unmappedMessages: string[] = [];

  if (Array.isArray(backendErrors)) {
    backendErrors.forEach((errStr: string) => {
      const lower = String(errStr).toLowerCase();
      if (lower.includes('name')) {
        fieldErrors.name = errStr;
      } else if (lower.includes('thumbnail') || lower.includes('image')) {
        fieldErrors.image_path = errStr;
      } else if (lower.includes('difficulty')) {
        fieldErrors.difficulty = errStr;
      } else if (lower.includes('category') || lower.includes('categories')) {
        fieldErrors.categories = errStr;
      } else if (lower.includes('description')) {
        fieldErrors.description = errStr;
      } else {
        unmappedMessages.push(errStr);
      }
    });
  } else if (typeof backendErrors === 'object' && backendErrors !== null) {
    if (backendErrors.name) {
      fieldErrors.name = Array.isArray(backendErrors.name) ? backendErrors.name[0] : backendErrors.name;
    }
    if (backendErrors.description) {
      fieldErrors.description = Array.isArray(backendErrors.description) ? backendErrors.description[0] : backendErrors.description;
    }
    if (backendErrors.difficulty) {
      fieldErrors.difficulty = Array.isArray(backendErrors.difficulty) ? backendErrors.difficulty[0] : backendErrors.difficulty;
    }
    if (backendErrors.categories) {
      fieldErrors.categories = Array.isArray(backendErrors.categories) ? backendErrors.categories[0] : backendErrors.categories;
    }
    if (backendErrors.image_path) {
      fieldErrors.image_path = Array.isArray(backendErrors.image_path) ? backendErrors.image_path[0] : backendErrors.image_path;
    }

    Object.keys(backendErrors).forEach((key) => {
      if (!['name', 'description', 'difficulty', 'categories', 'image_path'].includes(key)) {
        const val = backendErrors[key];
        unmappedMessages.push(Array.isArray(val) ? val[0] : String(val));
      }
    });
  } else if (typeof backendErrors === 'string') {
    unmappedMessages.push(backendErrors);
  }

  return {
    fieldErrors,
    generalError: unmappedMessages.length > 0 ? unmappedMessages.join(', ') : undefined
  };
};